import * as React from 'react';
import { PropsWithChildren } from 'react';
import { styled } from '../../theme';
import { DeviceMaxWidth } from '../../utils/device';
import { css } from '@emotion/react';
import { MarkdownWidget, UnsafeMarkdown } from '../MarkdownWidget';
import { Product } from '@journeyapps-platform/types-accounts';
import { ProductContext } from '../product/context';
import { LoaderSpinner } from '../LoaderWidget';

const logo_platform = require('../../../media/journeyapps-platform-logo-dark-background.png');
const logo_powersync = require('../../../media/powersync-logo-wording.svg');

export enum CenterFormOrientation {
  COLUMN = 'column',
  ROW = 'row'
}

export interface CenterFormWidgetProps {
  primaryLabel?: string;
  secondaryLabel?: string | UnsafeMarkdown;
  orientation?: CenterFormOrientation;
  noLogo?: boolean;
  className?: any;
  loading?: boolean;
  footer?: JSX.Element;
}

export const getProductLogo = (product: Product) => {
  if (product === Product.POWERSYNC) {
    return logo_powersync;
  }

  return logo_platform;
};

export const CenterFormWidget: React.FC<PropsWithChildren<CenterFormWidgetProps>> = (props) => {
  const getSecondaryLabel = (): JSX.Element | null => {
    if (!props.secondaryLabel) {
      return null;
    }
    if (props.secondaryLabel instanceof UnsafeMarkdown) {
      return (
        <S.LabelSecondary>
          <MarkdownWidget content={props.secondaryLabel} />
        </S.LabelSecondary>
      );
    }
    return <S.LabelSecondary>{props.secondaryLabel}</S.LabelSecondary>;
  };

  return (
    <ProductContext.Consumer>
      {(product) => {
        return (
          <S.CenterContainer>
            <S.Container className={props.className} orientation={props.orientation || CenterFormOrientation.COLUMN}>
              <S.Center>
                {!props.noLogo ? <S.Logo src={getProductLogo(product.product)} /> : null}
                <S.Label>{props.primaryLabel}</S.Label>
                {getSecondaryLabel()}
              </S.Center>
              {props.loading ? (
                <S.LoadingContainer>
                  <LoaderSpinner />
                </S.LoadingContainer>
              ) : (
                props.children
              )}
            </S.Container>
            {props.footer}
          </S.CenterContainer>
        );
      }}
    </ProductContext.Consumer>
  );
};

namespace S {
  const padding = 32;

  const col_css = css`
    max-width: calc(300px + 2 * ${padding}px);
  `;

  export const LoadingContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
  `;

  export const CenterContainer = styled.div`
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translateX(-50%) translateY(-50%);
  `;

  export const Container = styled.div<{ orientation: CenterFormOrientation }>`
    padding: ${padding}px;
    padding-top: 0px;
    background: rgba(44, 44, 46, 1);
    border-radius: 4px;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
    user-select: none;
    margin-bottom: 16px;

    ${(p) => (p.orientation == CenterFormOrientation.COLUMN ? col_css : '')};

    @media (${DeviceMaxWidth.mobileM}) {
      display: flex;
      flex-direction: column;
      justify-content: center;
      max-width: 100vw;
      min-height: 100vh;
      margin-bottom: 0px;
    }
    @media (${DeviceMaxWidth.mobileS}) {
      padding: 32px;
    }
  `;

  export const Logo = styled.img`
    width: 280px;
    user-select: none;
    -webkit-user-drag: none;
    padding-top: ${padding}px;
  `;

  export const Label = styled.div`
    font-size: 24px;
    color: ${(p) => p.theme.text.primary};
    margin-top: 40px;
    text-align: center;
  `;

  export const LabelSecondary = styled.div`
    font-size: 18px;
    text-align: center;
    color: ${(p) => p.theme.text.secondary};
    margin-top: 12px;
  `;

  export const Center = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 24px;
  `;
}
